import { useState } from "react";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageSquare } from "lucide-react";
import { CommentForm } from "../leads/comments/CommentForm";
import { CommentList } from "../leads/comments/CommentList";
import { Comment } from "../leads/comments/types";
import { useAuth } from "@/hooks/useAuth";
import { getLanguage } from "@/utils/languageUtils";
import { vehicleCardUILabels, leadUILabels, companyUILabels, commonMiscLabels } from "@/i18n";

interface CommentsSectionProps {
  comments?: Comment[];
  onSubmitComment: (
    text: string,
    isPublic: boolean,
    mentions: string[],
  ) => Promise<void>;
  onUpdateComment?: (commentId: string, newText: string) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
  isSubmitting: boolean;
  title?: string;
  category?: "lead" | "vehicleCard" | "company";
  customerComment?: {
    text: string;
    timestamp: Date;
  };
  /** Shorter layout for side panels (e.g. company management) */
  compact?: boolean;
  /** Extra-tight layout – only used with compact (e.g. company settings panel) */
  dense?: boolean;
}

export const CommentsSection = ({
  comments: initialComments = [],
  onSubmitComment,
  onUpdateComment,
  onDeleteComment,
  isSubmitting,
  title,
  category = "lead",
  customerComment,
  compact = false,
  dense = false,
}: CommentsSectionProps) => {
  const { user } = useAuth();
  const commonLabels = commonMiscLabels(getLanguage());
  const defaultTitle = title || commonLabels.comments;
  const noCommentsYetLabel =
    category === "company"
      ? companyUILabels(getLanguage()).noCommentsYet
      : category === "vehicleCard"
      ? vehicleCardUILabels(getLanguage()).noCommentsYet
      : leadUILabels(getLanguage()).noCommentsYet;
  // Backend sends timestamp as ISO string, CommentList.tsx handles formatting
  const [comments, setComments] = useState<Comment[]>(initialComments);

  // Update comments when initialComments change (e.g., after refetch)
  React.useEffect(() => {
    setComments(initialComments);
  }, [initialComments]);

  const handleSubmitComment = async (
    text: string,
    isPublic: boolean,
    mentions: string[],
  ) => {
    // Get author name from user - combine firstName and lastName, fallback to email
    const authorName = user
      ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email
      : "Unknown User";

    // Add comment to local state for immediate UI update (optimistic update)
    const optimisticComment: Comment = {
      id: Date.now().toString(),
      text,
      timestamp: new Date(),
      author: authorName,
      mentions,
      isPublic,
      isFailed: false,
      isLoading: true, // Mark as loading until we get success response
    };

    // Add new comment to the end of the list (oldest first)
    setComments((prev) => [...prev, optimisticComment]);

    try {
      await onSubmitComment(text, isPublic, mentions);

      // Mark comment as successfully sent (remove loading state)
      setComments((prev) =>
        prev.map((comment) =>
          comment.id === optimisticComment.id
            ? { ...comment, isLoading: false }
            : comment,
        ),
      );
    } catch (error) {
      // Mark the optimistic comment as failed instead of replacing it
      setComments((prev) =>
        prev.map((comment) =>
          comment.id === optimisticComment.id
            ? { ...comment, isFailed: true, isLoading: false }
            : comment,
        ),
      );
    }
  };

  const handleUpdateComment = async (commentId: string, newText: string) => {
    if (!onUpdateComment) return;

    const prev = comments;
    setComments((cur) =>
      cur.map((c) => (c.id === commentId ? { ...c, text: newText } : c)),
    );

    try {
      await onUpdateComment(commentId, newText);
    } catch (error) {
      setComments(prev); // rollback
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!onDeleteComment) return;

    const prev = comments;
    setComments((cur) => cur.filter((c) => c.id !== commentId));

    try {
      await onDeleteComment(commentId);
    } catch (error) {
      setComments(prev); // rollback
    }
  };

  return (
    <Card className={compact ? "flex flex-col min-h-0 h-full" : undefined}>
      <CardHeader
        className={
          compact
            ? dense
              ? "py-1 px-2 shrink-0"
              : "py-2 px-3 shrink-0"
            : undefined
        }
      >
        <CardTitle>
          <div
            className={`flex items-center ${dense ? "gap-1 text-xs font-semibold" : compact ? "gap-1.5 text-sm font-semibold" : "gap-1.5 text-lg"}`}
          >
            <MessageSquare
              className={dense ? "h-3.5 w-3.5" : compact ? "h-4 w-4" : "h-5 w-5"}
            />
            {defaultTitle}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent
        className={`flex flex-col min-h-0 ${compact ? `flex-1 ${dense ? "px-2 pb-2 pt-0" : "px-3 pb-3 pt-0"}` : "h-[600px]"}`}
      >
        {/* Comments area - takes up remaining space */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <CommentList
            comments={comments}
            noCommentsYet={noCommentsYetLabel}
            customerComment={customerComment}
            onUpdateComment={onUpdateComment ? handleUpdateComment : undefined}
            onDeleteComment={onDeleteComment ? handleDeleteComment : undefined}
            compact={compact}
            dense={dense}
          />
        </div>

        {/* Input form - always at bottom */}
        <div
          className={
            compact
              ? dense
                ? "mt-1 pt-1.5 border-t shrink-0"
                : "mt-2 pt-2 border-t shrink-0"
              : "mt-4 pt-4 border-t"
          }
        >
          <CommentForm
            onSubmit={handleSubmitComment}
            isSubmitting={isSubmitting}
            dense={dense}
          />
        </div>
      </CardContent>
    </Card>
  );
};
